import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Search, AlertTriangle, Home, Twitter, Youtube, Facebook, Instagram } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Header({ onSearch }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [socialLinks, setSocialLinks] = useState({ twitter: '', youtube: '', facebook: '', instagram: '' });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchSocialLinks();
  }, []);

  const fetchSocialLinks = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/social`);
      setSocialLinks(data);
    } catch (error) {
      console.error('Error fetching social links:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(searchQuery);
    }
  };

  const handleHomeClick = (e) => {
    e.preventDefault();
    setSearchQuery('');
    if (location.pathname === '/') {
      // Already on home: clear search filter and scroll to top
      if (onSearch) onSearch('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // On another route (e.g. /article/:id): navigate back to home
      navigate('/');
      window.scrollTo({ top: 0 });
    }
  };

  const categories = [
    { name: 'Violent Crimes', slug: 'violent' },
    { name: 'Cyber Crimes', slug: 'cyber' },
    { name: 'Financial', slug: 'white-collar' },
    { name: 'Family/Extramarital', slug: 'property' },
    { name: 'Tragedy', slug: 'tragedy' },
    { name: 'Others', slug: 'others' }
  ];

  const socialIcons = [
    { key: 'twitter', url: socialLinks.twitter, Icon: Twitter, label: 'Twitter', color: '#1DA1F2', hoverBg: 'hover:bg-[#1DA1F2]' },
    { key: 'youtube', url: socialLinks.youtube, Icon: Youtube, label: 'YouTube', color: '#FF0000', hoverBg: 'hover:bg-[#FF0000]' },
    { key: 'facebook', url: socialLinks.facebook, Icon: Facebook, label: 'Facebook', color: '#1877F2', hoverBg: 'hover:bg-[#1877F2]' },
    { key: 'instagram', url: socialLinks.instagram, Icon: Instagram, label: 'Instagram', color: '#E4405F', hoverBg: 'hover:bg-[#E4405F]' }
  ];

  return (
    <header className="bg-[#FF4500] border-b border-[#CC3700] sticky top-0 z-50">
      <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg, #DC2626 0%, #D97706 25%, #FFB703 50%, #059669 75%, #7C3AED 100%)' }} data-testid="header-accent-bar"></div>
      <div className="max-w-[1600px] mx-auto px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
            <img
              src="/logo.jpeg"
              alt="Crime Newz"
              className="h-14 w-14 sm:h-16 sm:w-16 object-contain bg-white rounded-md p-1 shadow-sm"
            />
            <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-white hidden sm:block">
              CRIME NEWZ
            </h1>
          </Link>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 pr-2 border-r border-white/30" data-testid="header-social-icons">
              {socialIcons.map(({ key, url, Icon, label, color }) => (
                url ? (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={`social-${key}-link`}
                    style={{ color: color }}
                    className="w-9 h-9 flex items-center justify-center bg-white rounded-full hover:scale-110 transition-transform shadow-sm"
                  >
                    <Icon size={16} />
                  </a>
                ) : (
                  <span
                    key={key}
                    aria-label={`${label} (not configured)`}
                    data-testid={`social-${key}-link-disabled`}
                    style={{ color: color }}
                    className="w-9 h-9 flex items-center justify-center bg-white rounded-full cursor-not-allowed shadow-sm"
                    title={`${label} not configured — add URL in Admin → Settings`}
                  >
                    <Icon size={16} />
                  </span>
                )
              ))}
            </div>

            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                placeholder="Search news..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="header-search-input"
                className="w-64 px-4 py-2 pr-10 border border-white/40 bg-white/95 focus:outline-none focus:border-white transition-colors"
                
              />
              <button
                type="submit"
                data-testid="header-search-button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#525252] hover:text-[#FF4500] transition-colors"
              >
                <Search size={20} />
              </button>
            </form>

            <Link
              to="/admin"
              data-testid="header-admin-link"
              className="flex items-center gap-2 px-4 py-2 bg-white text-[#FF4500] font-bold text-sm tracking-wider hover:bg-[#1A1A2E] hover:text-white transition-colors"
              
            >
              <AlertTriangle size={16} />
              ADMIN
            </Link>
          </div>
        </div>

        <nav className="flex items-center gap-8 pt-2">
          <a
            href="/"
            onClick={handleHomeClick}
            data-testid="nav-home-link"
            className="flex items-center gap-1.5 text-base tracking-[0.15em] uppercase font-black text-white hover:text-[#FFE4B5] transition-colors relative group"
            
          >
            <Home size={18} />
            Home
            <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-white group-hover:w-full transition-all duration-300"></span>
          </a>
          {categories.map((cat) => (
            <a
              key={cat.slug}
              href={`#${cat.slug}`}
              data-testid={`nav-${cat.slug}-link`}
              className="text-base tracking-[0.15em] uppercase font-black text-white hover:text-[#FFE4B5] transition-colors relative group"
              
            >
              {cat.name}
              <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-white group-hover:w-full transition-all duration-300"></span>
            </a>
          ))}
        </nav>
      </div>
    </header>
  );
}

export default Header;
